import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import * as React from "react";
import { fixMediaUrl as fixDrupalMediaUrl } from "../api/drupal/drupalApi";
import { imageConfig } from "../features/image";
import { isNullOrWhitespace } from "../utils/string";
const fixMediaUrl = (style_url)=>{
    if (isNullOrWhitespace(style_url)) return style_url;
    let fixedUrl = fixDrupalMediaUrl(style_url);
    imageConfig().replacements.forEach((replacement)=>{
        fixedUrl = fixedUrl.replace(replacement.replace, replacement.with);
    });
    return fixedUrl;
};
/**
 * Renders a responsive `<picture>` element with multiple `<source>` elements for different media queries,
 * and a fallback `<img>` element. Supports lazy loading and custom image crops per media.
 *
 * @param props - The properties for the responsive picture component.
 * @param props.defaultImageUrl - The default image URL to use as the fallback or main image.
 * @param props.className - Optional class name for the `<picture>` element.
 * @param props.imgClassName - Optional class name for the `<img>` element.
 * @param props.alt - Alternative text for the image.
 * @param props.width - Width of the image.
 * @param props.height - Height of the image.
 * @param props.cropsPerMedia - Array of crop configurations for different media queries.
 * @param props.ariaHidden - If true, sets `aria-hidden` attribute on the `<picture>` element.
 * @param props.lazyload - If true, enables lazy loading for the image (default: true).
 *
 * @returns A JSX element rendering a responsive picture with sources and a fallback image.
 */ export const ResponsivePicture = (props)=>{
    const { className, imgClassName, alt, width, height, cropsPerMedia, ariaHidden } = props;
    const { lazyload = true } = props;
    const placeholder = imageConfig().emptyImageUrl;
    const defaultImageUrl = fixMediaUrl(props.defaultImageUrl);
    const defaultSource = lazyload ? {
        src: placeholder,
        "data-src": defaultImageUrl
    } : {
        src: defaultImageUrl
    };
    return /*#__PURE__*/ _jsxs("picture", {
        ...className ? {
            className
        } : {},
        'aria-hidden': ariaHidden,
        children: [
            cropsPerMedia.map((src, i)=>/*#__PURE__*/ _jsx("source", {
                    media: src.media,
                    ...sourcesAndSizes(src, lazyload)
                }, `crop-${i}`)),
            /*#__PURE__*/ _jsx("img", {
                className: `${imgClassName || ''}${lazyload ? ' lazyload' : ' no-lazyload'}`,
                alt,
                width,
                height,
                ...defaultSource
            })
        ]
    });
};
const sourcesAndSizes = (src, lazyload)=>{
    const srcSet = src.crops.map((param)=>{
        let { url, width } = param;
        const widthProp = width == 1 ? 'x' : 'w';
        return `${fixMediaUrl(url)} ${width}${widthProp}`;
    }).join(', ');
    const sizes = src.sizes || 'auto';
    return lazyload ? {
        'data-srcset': srcSet,
        'data-sizes': sizes
    } : {
        srcSet,
        sizes
    };
};
export const singleCrop = (url)=>[
        {
            crops: [
                {
                    url: fixMediaUrl(url),
                    width: 1
                }
            ]
        }
    ];
